<template>
  <div v-if="visible" class="dialog-overlay">
    <div class="dialog-content">
      <!-- 标题区域 -->
      <div class="dialog-header">
        <h3 class="dialog-title">{{ title }} ({{ currentIndex + 1 }}/{{ files.length }})</h3>
        <button class="close-button" @click="closeDialog">×</button>
      </div>

      <!-- 导航按钮移动到标题下方 -->
      <div class="navigation-buttons">
        <button 
          class="nav-button prev-button" 
          @click="switchFile(-1)"
          :disabled="currentIndex <= 0"
        >
          <span class="button-text">上一张</span>
        </button>
        <button 
          class="nav-button next-button" 
          @click="switchFile(1)"
          :disabled="currentIndex >= files.length - 1"
        >
          <span class="button-text">下一张</span>
        </button>
      </div>

<!-- 文件信息行（图标 + 文件名 + 原文件链接） -->
      <div class="file-info">
        <span class="file-icon">
          <img
            v-if="isImage"
            src="../../assets/image.png"
            alt="图片图标"
            class="icon-img"
          />
          <img
            v-else-if="isExcel"
            src="../../assets/xlsx_icon.png"
            alt="表格图标"
            class="icon-img"
          />
          <img
            v-else-if="isText"
            src="../../assets/txt.jpg"
            alt="文本图标"
            class="icon-img"
          />
        </span>
        <span class="file-name">{{ fileName }}</span>
        <a class="download-link" @click="downloadFile">原文件</a>
      </div>
     <!-- 内容区域 -->
      <div class="dialog-body">
        <!-- 图片预览 -->
        <!-- <div v-if="isImage" class="preview-container image-preview">
          <img :src="fileUrl" alt="Preview" />
        </div> -->
        <div v-if="isImage">
          <!-- 普通预览模式 -->
          <div 
            class="preview-container image-preview"
            @dblclick="toggleFullscreen"
          >
            <img 
              :src="fileUrl" 
              alt="Preview"
              class="preview-image"
              title="双击全屏预览"
            />
          </div>

          <!-- 全屏预览模式 -->
          <div v-if="isFullscreen" class="fullscreen-overlay" @dblclick="toggleFullscreen">
            <div class="close-button-full" @click="toggleFullscreen">×</div>
            <img
              :src="fileUrl"
              alt="Fullscreen Preview"
              :style="fullscreenStyle"
              class="fullscreen-image"
              @wheel.prevent="handleWheel"
              @mousedown="startDrag"
              @mousemove="doDrag"
              @mouseup="endDrag"
              @mouseleave="endDrag"
            />
          </div>
        </div>

        <!-- 文本预览 -->
        <div v-if="isText" class="preview-container text-preview">
          <pre>{{ fileContent }}</pre>
        </div>

        <!-- Excel 表格预览 -->
        <div v-if="isExcel" class="preview-container table-preview">
          <table>
            <thead>
              <tr>
                <th v-for="(header, headerIndex) in excelHeaders" :key="`header-${headerIndex}`">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rowIndex) in excelData" :key="`row-${rowIndex}`">
                <td v-for="(cell, cellIndex) in row" :key="`cell-${rowIndex}-${cellIndex}`">
                  {{ cell }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default {
  props: {
    files: {
      type: Array,
      default: () => [],
      validator: value => value.every(f => f.url) // 确保每个文件有url属性
    },
    visible: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: '文件预览',
    },
    initialIndex: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      fileContent: '',
      excelData: [],
      excelHeaders: [],
      currentIndex: this.initialIndex,
      isFullscreen: false,
      scale: 1,
      rotation: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      translateX: 0,
      translateY: 0,
      lastTranslate: { x: 0, y: 0 }
    };
  },
  mounted() {
    // ESC键退出全屏
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isFullscreen) {
        this.toggleFullscreen()
      }
    })
  },
  computed: {
    isText() {
      return this.fileUrl.match(/\.(txt)$/) != null;
    },
    isExcel() {
      return this.fileUrl.match(/\.(xlsx|xls)$/) != null;
    },
    currentFile() {
      return this.files[this.currentIndex] || {};
    },
    fileUrl() {
      return this.currentFile.url || '';
    },
    // 修改原有的fileName计算属性
    fileName() {
      const url = this.fileUrl;
      const segments = url.split('/');
      return segments[segments.length - 1] || '未命名文件';
    },
    // 保持其他计算属性，修改匹配逻辑使用当前文件的url
    isImage() {
      return this.fileUrl.match(/\.(jpeg|jpg|gif|png)$/) != null;
    },
    imgStyle() {
      return {
        transform: `scale(${this.scale}) rotate(${this.rotation}deg)`,
        transition: 'transform 0.3s ease'
      }
    },
    fullscreenStyle() {
      return {
        transform: `
          translate(${this.translateX}px, ${this.translateY}px)
          scale(${this.scale}) 
          rotate(${this.rotation}deg)
        `,
        cursor: this.isDragging ? 'grabbing' : 'grab'
      }
    }
  },
  watch: {
    // 修改监听器为currentFile
    currentFile: {
      immediate: true,
      handler(newFile) {
        if (newFile.url) {
          this.loadFileContent(newFile.url);
        }
      }
    },
    // 添加files监听
    files(newFiles) {
      if (newFiles.length > 0) {
        this.currentIndex = Math.min(this.initialIndex, newFiles.length - 1);
      }
    }
  },
  methods: {
    switchFile(offset) {
      const newIndex = this.currentIndex + offset;
      if (newIndex >= 0 && newIndex < this.files.length) {
        this.currentIndex = newIndex;
      }
    },
    // 加载文件内容
    async loadFileContent(url) {
      if (this.isImage) return;

      try {
        const response = await axios.get(url, { responseType: 'arraybuffer' });
        if (this.isText) {
          this.fileContent = new TextDecoder('utf-8').decode(response.data);
        } else if (this.isExcel) {
          const workbook = XLSX.read(response.data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          this.excelData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          this.excelHeaders = this.excelData[0];
          this.excelData = this.excelData.slice(1);
        }
      } catch (error) {
        console.error('Error loading file content:', error);
      }
    },
   // 修改下载方法使用当前文件
    async downloadFile() {
      const link = document.createElement('a')
      link.href = this.fileUrl
      link.download = this.fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    // 关闭对话框
    closeDialog() {
      this.$emit('close');
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      if (!this.isFullscreen) {
        this.resetTransform()
      }
    },
    handleWheel(e) {
      const delta = e.deltaY > 0 ? 0.9 : 1.1
      const newScale = this.scale * delta
      
      // 限制缩放范围
      if (newScale > 0.2 && newScale < 5) {
        // 计算基于鼠标位置的缩放
        const rect = e.target.getBoundingClientRect()
        const offsetX = e.clientX - rect.left
        const offsetY = e.clientY - rect.top
        
        this.translateX += (offsetX - this.translateX) * (1 - delta)
        this.translateY += (offsetY - this.translateY) * (1 - delta)
        
        this.scale = newScale
      }
    },
     startDrag(e) {
      this.isDragging = true
      this.dragStartX = e.clientX - this.translateX
      this.dragStartY = e.clientY - this.translateY
    },
    doDrag(e) {
      if (!this.isDragging) return
      this.translateX = e.clientX - this.dragStartX
      this.translateY = e.clientY - this.dragStartY
    },
    endDrag() {
      this.isDragging = false
      this.lastTranslate = { 
        x: this.translateX, 
        y: this.translateY 
      }
    },
    resetTransform() {
      this.scale = 1
      this.rotation = 0
      this.translateX = 0
      this.translateY = 0
    },

  },
};
</script>

<style scoped>
/* 对话框样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.dialog-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 800px; /* 固定宽度 */
  max-height: 90vh; /* 最大高度为视口的80% */
  display: flex;
  flex-direction: column;
}

.dialog-header {
  position: relative;
  margin-bottom: 15px;
  text-align: center;
}

.dialog-title {
  margin: 0;
  font-size: 20px;
}

.close-button {
  position: absolute;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 5px;
  top: -10px;
  right: -10px;
}

/* 文件信息行样式 */
.file-info {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.file-icon .icon-img {
  width: 24px;
  height: 24px;
  margin-right: 10px;
}

.file-name {
  font-size: 14px;
  color: #666;
  max-width: 60%; /* 调整宽度，为链接留出空间 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.download-link {
  margin-left: 10px;
  font-size: 14px;
  color: #007bff; /* 链接颜色 */
  cursor: pointer;
  text-decoration: underline;
}

.download-link:hover {
  color: #0056b3; /* 鼠标悬停颜色 */
}

/* 内容区域样式 */
.dialog-body {
  flex: 1; /* 内容区域填充剩余空间 */
  overflow-y: auto; /* 内容超出时显示滚动条 */
}


.preview-container {
  margin-top: 10px;
}

.preview-image {
  cursor: zoom-in;
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  cursor: zoom-out;
}

.fullscreen-image {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  transition: transform 0.1s ease-out;
}

/* 禁用文本选择 */
img {
  user-select: none;
  -webkit-user-drag: none;
}


.text-preview pre {
  white-space: pre-wrap; /* 允许文本换行 */
  word-wrap: break-word;
  margin: 0;
}

.table-preview {
  overflow-x: auto; /* 表格内容超出时显示水平滚动条 */
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

th {
  background-color: #f2f2f2;
}

/* 新增导航按钮样式 */
.navigation-buttons {
  display: flex;
  justify-content: center;
  gap: 20px; /* 按钮之间的间距 */
  margin: 15px 0; /* 上下边距 */
}

.nav-button {
  background-color: #007bff; /* 按钮背景色 */
  color: white; /* 文字颜色 */
  border: none;
  border-radius: 4px; /* 圆角 */
  padding: 8px 16px; /* 内边距 */
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-button:disabled {
  background-color: #cccccc; /* 禁用时的背景色 */
  cursor: not-allowed;
  opacity: 0.7;
}

.nav-button:hover:not(:disabled) {
  background-color: #0056b3; /* 鼠标悬停时的背景色 */
}

/* 按钮文字样式 */
.button-text {
  margin: 0 4px; /* 文字与图标的间距 */
}


/* 新增关闭按钮样式 */
.close-button-full {
  position: fixed;
  top: 20px;
  right: 30px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 32px;
  line-height: 32px;
  text-align: center;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10000;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button-full:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}


</style>